'use client'

import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { CheckCircle2, XCircle, Info, X } from 'lucide-react'

type ToastKind = 'success' | 'error' | 'info'
type Toast = { id: number; kind: ToastKind; message: string }

type ToastCtx = {
  toast: (message: string, kind?: ToastKind) => void
  success: (message: string) => void
  error: (message: string) => void
}

const Ctx = createContext<ToastCtx | null>(null)

/* Most modułowy: pozwala warstwom nie-Reactowym (api-client) emitować toasty.
   Provider rejestruje się przy mount; dedupe chroni przed spamem przy seriach requestów. */
let busEmit: ((message: string, kind: ToastKind) => void) | null = null
let lastMsg = ''
let lastAt = 0
export function emitToast(message: string, kind: ToastKind = 'info') {
  const now = Date.now()
  if (message === lastMsg && now - lastAt < 1500) return
  lastMsg = message
  lastAt = now
  busEmit?.(message, kind)
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx)
  // Poza providerem (np. strony publiczne) — cichy no-op zamiast crasha
  if (!ctx) return { toast: () => {}, success: () => {}, error: () => {} }
  return ctx
}

const ICONS: Record<ToastKind, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
}

const STYLES: Record<ToastKind, string> = {
  success: 'border-success/40 text-success',
  error: 'border-danger/40 text-danger',
  info: 'border-info/40 text-info',
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, kind: ToastKind = 'info') => {
      const id = ++idRef.current
      setToasts((t) => [...t.slice(-3), { id, kind, message }])
      const ttl = kind === 'error' ? 5000 : 3200
      setTimeout(() => dismiss(id), ttl)
    },
    [dismiss]
  )

  const api: ToastCtx = {
    toast,
    success: (m) => toast(m, 'success'),
    error: (m) => toast(m, 'error'),
  }

  // rejestracja mostu dla api-client (ostatni provider wygrywa — mamy jeden)
  busEmit = toast

  return (
    <Ctx.Provider value={api}>
      {children}
      {/* aria-live: czytniki ekranu ogłaszają komunikat bez kradzieży fokusu */}
      <div
        aria-live="polite"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none px-4 w-full max-w-md"
      >
        {toasts.map((t) => {
          const Icon = ICONS[t.kind]
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex items-center gap-2.5 w-full sm:w-auto sm:min-w-[280px] max-w-full
                          rounded-xl border bg-white dark:bg-installer-card shadow-medium
                          px-4 py-3 text-[13px] font-medium text-dark dark:text-white
                          animate-toast-in ${STYLES[t.kind]}`}
            >
              <Icon size={17} className="shrink-0" />
              <span className="flex-1 text-dark dark:text-white">{t.message}</span>
              <button
                onClick={() => dismiss(t.id)}
                aria-label="Zamknij powiadomienie"
                className="shrink-0 text-gray-1 hover:text-dark dark:hover:text-white transition-colors cursor-pointer"
              >
                <X size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}
